import React, { useEffect, useState } from "react";
import Chart from "react-apexcharts";
import jsonData from "../../../../current-survey-numeric.json"; // Ensure the path is correct

const TopLeftChart = ({ filterCriteria }) => {
  const [filteredScores, setFilteredScores] = useState({
    NR: 0,
    1: 0,
    2: 0,
    3: 0,
    4: 0,
    5: 0,
  });

  const [unfilteredScores, setUnfilteredScores] = useState({
    NR: 0,
    1: 0,
    2: 0,
    3: 0,
    4: 0,
    5: 0,
  });

  useEffect(() => {
    const processScores = (data) => {
      const scores = {
        NR: 0,
        1: 0,
        2: 0,
        3: 0,
        4: 0,
        5: 0,
      };

      data.forEach((item) => {
        const score = item.MQ0009;
        if (
          score === 1 ||
          score === 2 ||
          score === 3 ||
          score === 4 ||
          score === 5
        ) {
          scores[score]++;
        } else {
          scores.NR++;
        }
      });

      return scores;
    };

    // Filter the data based on the provided filterCriteria
    const filteredData = jsonData.filter((item) => {
      return Object.keys(filterCriteria).every(
        (key) =>
          filterCriteria[key].length === 0 ||
          filterCriteria[key].includes(item[key])
      );
    });

    const newFilteredScores = processScores(filteredData);
    const newUnfilteredScores = processScores(jsonData);

    setFilteredScores(newFilteredScores); // Set state for filtered scores
    setUnfilteredScores(newUnfilteredScores); // Set state for unfiltered scores
  }, [filterCriteria]); // Recalculate whenever filterCriteria changes

  const labelToScoreKeyMap = {
    "Intensely Dislike": 1,
    "Do Not Like": 2,
    "Neither Like or Dislike": 3,
    "Like my work": 4,
    "Love my work": 5,
    "NR": "NR",
  };

  const seriesData = Object.keys(filteredScores).map((key) => {
    return {
      x: key,
      y: filteredScores[key], // Ensure the filtered scores are used for data
      goals: [
        {
          name: "Unfiltered",
          value: unfilteredScores[key], // Use unfiltered scores for goals
          strokeHeight: 12,
          strokeColor: "#6D6D6D",
        },
      ],
    };
  });

  const options = {
    chart: {
      type: "bar",
      height: "300px",
    },
    colors: ["#F28E2B", "#F28E2B", "#F1CE63", "#499894", "#499894", "#BAB0AC"],
    plotOptions: {
      bar: {
        borderRadius: 4,
        borderRadiusApplication: "end",
        horizontal: true,
        distributed: true,
        dataLabels: {
          position: "top",
        },
      },
    },
    dataLabels: {
      enabled: true,
      formatter: function (val, opt) {
        const xAxisLabel = opt.w.globals.labels[opt.dataPointIndex];
        const scoreKey = labelToScoreKeyMap[xAxisLabel];
        const filteredValue = filteredScores[scoreKey] || 0; // Get the filtered value

        const totalFiltered = Object.values(filteredScores).reduce(
          (acc, curr) => acc + curr,
          0
        ); // Calculate total filtered
        const unfilteredValue = unfilteredScores[scoreKey] || 0; // Get unfiltered value
        const totalUnfiltered = Object.values(unfilteredScores).reduce(
          (acc, curr) => acc + curr,
          0
        ); // Calculate total unfiltered

        const filteredPercentage =
          totalFiltered > 0
            ? ((filteredValue / totalFiltered) * 100).toFixed(2)
            : "0.00"; // Calculate filtered percentage
        const unfilteredPercentage =
          totalUnfiltered > 0
            ? ((unfilteredValue / totalUnfiltered) * 100).toFixed(2)
            : "0.00"; // Calculate unfiltered percentage

        return `${filteredPercentage}% | ${unfilteredPercentage}%`; // Return formatted data label
      },
      style: {
        colors: ["#000"], // Set data labels to black
      },
      offsetX: 65, // Move labels off the bars
    },
    xaxis: {
      categories: [
        "Intensely Dislike",
        "Do Not Like",
        "Neither Like or Dislike",
        "Like my work",
        "Love my work",
        "NR",
      ],
    },
    tooltip: {
      enabled: true,
      shared: true, // Ensure the tooltip is shared across series
      intersect: false, // Set intersect to false to avoid errors
      custom: function ({ series, seriesIndex, dataPointIndex, w }) {
        const xAxisLabel = w.globals.labels[dataPointIndex]; // Get the x-axis label
        const scoreKey = labelToScoreKeyMap[xAxisLabel]; // Map x-axis label to score key
        const filteredValue = filteredScores[scoreKey] || 0; // Get the filtered value
        const totalFiltered = Object.values(filteredScores).reduce(
          (acc, curr) => acc + curr,
          0
        ); // Calculate total filtered
        const unfilteredValue = unfilteredScores[scoreKey] || 0; // Get unfiltered value
        const totalUnfiltered = Object.values(unfilteredScores).reduce(
          (acc, curr) => acc + curr,
          0
        ); // Calculate total unfiltered

        const filteredPercentage =
          totalFiltered > 0
            ? ((filteredValue / totalFiltered) * 100).toFixed(2)
            : "0.00"; // Calculate filtered percentage
        const unfilteredPercentage =
          totalUnfiltered > 0
            ? ((unfilteredValue / totalUnfiltered) * 100).toFixed(2)
            : "0.00"; // Calculate unfiltered percentage

        return `<div class="apexcharts-tooltip-text">
            <div id="tooltip-title">Response:&nbsp; ${xAxisLabel}</div>  
            <ul>
            <li>Filtered: &nbsp; ${filteredPercentage}%</li>
            <li>Unfiltered: &nbsp; ${unfilteredPercentage}%</li>
            </ul>
          </div>`;
      },
    },
    legend: {
      show: false,
      labels: {
        useSeriesColors: true,
      },
    },
    annotations: {
      points: Object.keys(unfilteredScores).map((key, index) => {
        return {
          x: key,
          y: unfilteredScores[key],
          marker: {
            size: 5, // Make the marker thicker
            fillColor: "#fff",
            strokeColor: "#fff",
          },
          label: {
            borderColor: "#fff",
            offsetY: 0,
            style: {
              color: "#fff",
              background: "#fff",
            },
            text: `${unfilteredScores[key]}`,
          },
        };
      }),
    },
  };

  return (
    <div id="d08-top-left">
      <Chart
        options={options}
        series={[{ name: "Filtered", data: seriesData }]} // Use filtered data for the chart
        type="bar"
        height="300px"
      />
    </div>
  );
};

export default TopLeftChart;
